import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from 'lib/utils'

type DrawerDirection = 'top' | 'bottom' | 'left' | 'right'

const DrawerContext = React.createContext<{ direction: DrawerDirection }>({ direction: 'bottom' })

const Drawer = ({
  shouldScaleBackground = false,
  direction = 'bottom',
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & { direction?: DrawerDirection }) => (
  <DrawerContext.Provider value={{ direction }}>
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      direction={direction}
      handleOnly={direction === 'right' || direction === 'left'}
      {...props}
    >
      {children}
    </DrawerPrimitive.Root>
  </DrawerContext.Provider>
)
Drawer.displayName = 'Drawer'

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerHandle = DrawerPrimitive.Handle

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
    variant?: 'default' | 'dark'
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50',
      variant === 'default' && 'bg-black/40',
      variant === 'dark' && 'bg-black/70 backdrop-blur-sm',
      className
    )}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

type DrawerVariant = 'default' | 'dark'

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    showOverlay?: boolean
    /**
     * `default` — legacy white drawer (`bg-background`). `dark` — T3 dual-theme
     * drawer (paper text on `bg-2`, hairline `--line` border, soft shadow).
     * Use `dark` for any new mobile sheet in a migrated surface.
     */
    variant?: DrawerVariant
  }
>(({ className, children, showOverlay = true, variant = 'default', ...props }, ref) => {
  const { direction } = React.useContext(DrawerContext)

  return (
    <DrawerPortal>
      {showOverlay && <DrawerOverlay variant={variant} />}
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 flex flex-col select-text',
          // Default — legacy white surface with default shadcn border.
          variant === 'default' && 'border bg-background',
          // T3 — paper text on bg-2 with hairline --line and tokenized shadow.
          variant === 'dark' && 'border border-line bg-bg-2 text-paper shadow-[var(--shadow-card)]',
          // Bottom drawer - snap points control height, don't set h-auto
          direction === 'bottom' && 'inset-x-0 bottom-0 rounded-t-[16px]',
          // Top drawer - rounded bottom corners
          direction === 'top' && 'pt-safe inset-x-0 top-0 mb-24 h-auto rounded-b-[16px]',
          // Right drawer - full screen on mobile, rounded on desktop
          direction === 'right' &&
            'inset-y-0 right-0 h-full w-full sm:w-3/4 sm:max-w-lg sm:rounded-l-[16px]',
          // Left drawer - full screen on mobile, rounded on desktop
          direction === 'left' &&
            'inset-y-0 left-0 h-full w-full sm:w-3/4 sm:max-w-lg sm:rounded-r-[16px]',
          className
        )}
        style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
        {...props}
      >
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
})
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-lg leading-none font-semibold tracking-tight', className)}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHandle,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
}
